import { useEffect, useMemo, useRef, useState } from "react";
import type { Transcript, TranscriptState, TranscriptSummary } from "../types";
import {
  canRetry,
  formatDateTime,
  formatTimestamp,
  humaneError,
  isAllowedAudioName,
  retryConfirmationMessage,
} from "./helpers";
import { reportError } from "./report";
import { pickAudioFormat, type RecorderStatus, useRecorder } from "./recorder";

const CHIP: Record<TranscriptState, { cls: string; label: string }> = {
  processing: { cls: "chip chip-processing", label: "Transcribing" },
  done: { cls: "chip chip-done", label: "Done" },
  failed: { cls: "chip chip-failed", label: "Failed" },
};

interface LibraryProps {
  transcripts: TranscriptSummary[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onUploaded: () => void;
  onChanged: () => void; // rename/delete happened — reload the list
  maxRecordingMinutes: number;
}

export function recordDotClass(status: RecorderStatus): string {
  return status === "recording" ? "rec-dot recording" : "rec-dot";
}

export function Library({
  transcripts,
  selectedId,
  onSelect,
  onUploaded,
  onChanged,
  maxRecordingMinutes,
}: LibraryProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [menuId, setMenuId] = useState<string | null>(null); // row whose action menu is open

  // Close the open action menu on outside-click or Escape (mirrors UserMenu).
  useEffect(() => {
    if (!menuId) return;
    const onDown = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest(".item-menu")) setMenuId(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuId(null);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuId]);

  async function rename(t: TranscriptSummary) {
    setMenuId(null);
    const next = prompt("Rename recording", t.name)?.trim();
    if (!next || next === t.name) return;
    const res = await fetch(`/api/transcripts/${t.id}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ name: next }),
    });
    if (res.ok) onChanged();
    else setError("Rename failed.");
  }

  async function remove(t: TranscriptSummary) {
    setMenuId(null);
    if (!confirm(`Delete "${t.name}". This cannot be undone.`)) return;
    const res = await fetch(`/api/transcripts/${t.id}`, { method: "DELETE" });
    if (res.ok) onChanged();
    else setError("Delete failed.");
  }

  async function retry(t: TranscriptSummary) {
    setMenuId(null);
    if (!confirm(retryConfirmationMessage(t.name))) return;
    setError(null);
    const res = await fetch(`/api/transcripts/${t.id}/retry`, { method: "POST" });
    if (res.ok) onChanged();
    else {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      setError(body.error ?? "Retry failed.");
    }
  }

  // Copy the transcript text (speaker-labelled lines) when it's ready; fall back
  // to the recording name for a still-processing or failed one.
  async function copy(t: TranscriptSummary) {
    setMenuId(null);
    try {
      let text = t.name;
      if (t.state === "done") {
        const res = await fetch(`/api/transcripts/${t.id}`);
        if (res.ok) {
          const full = (await res.json()) as Transcript;
          text =
            full.segments.map((s) => (s.speaker ? `${s.speaker}: ` : "") + s.text).join("\n") || t.name;
        }
      }
      await navigator.clipboard.writeText(text);
    } catch {
      setError("Copy failed.");
    }
  }

  async function handleFile(file: File, source: "uploaded" | "recorded" = "uploaded") {
    if (!isAllowedAudioName(file.name)) {
      setError("Unsupported file type. Pick an audio file (mp3, wav, m4a, …).");
      return;
    }
    setError(null);
    setUploading(true);
    try {
      // 1. ask voic for a presigned PUT URL
      const pres = await fetch("/api/uploads/presign", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ filename: file.name }),
      });
      if (!pres.ok) {
        const body = (await pres.json().catch(() => ({}))) as { error?: string };
        setError(body.error ?? `Upload failed (${pres.status})`);
        return;
      }
      const { key, uploadUrl } = (await pres.json()) as { key: string; uploadUrl: string };

      // 2. upload the bytes straight to object storage (bypasses voic)
      const put = await fetch(uploadUrl, { method: "PUT", body: file });
      if (!put.ok) {
        setError(`Upload to storage failed (${put.status})`);
        return;
      }

      // 3. register the object → server verifies it and starts transcription
      const reg = await fetch("/api/transcripts", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ key, name: file.name, source }),
      });
      if (!reg.ok) {
        const body = (await reg.json().catch(() => ({}))) as { error?: string };
        setError(body.error ?? `Upload failed (${reg.status})`);
        return;
      }
      onUploaded();
    } catch (err) {
      // A thrown fetch here = network/CORS (e.g. the cross-origin bucket PUT) — it
      // never reaches the server, so report it so the failure is visible backend-side.
      reportError(`upload failed name=${file.name}`, err);
      setError("Upload failed. Is the server running?");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  // A recorded take rides the same upload path as a picked file (handleFile),
  // tagged so usage stats can tell recordings from uploads.
  const recorder = useRecorder(maxRecordingMinutes * 60, (f) => handleFile(f, "recorded"));
  const canRecord = useMemo(() => pickAudioFormat() != null, []);
  const capSeconds = maxRecordingMinutes * 60;

  return (
    <>
      <div className="sidebar-head">
        {/* Record — browser mic capture, sits above the upload button */}
        {recorder.status === "recording" || recorder.status === "paused" ? (
          <div className="rec-panel">
            <div className="rec-live">
              <span className={recordDotClass(recorder.status)} aria-hidden />
              <span className="rec-time">{formatTimestamp(recorder.elapsed)}</span>
              <span className="rec-cap">/ {formatTimestamp(capSeconds)}</span>
            </div>
            <div className="rec-controls">
              <button type="button" className="btn-ghost" onClick={recorder.togglePause}>
                {recorder.status === "paused" ? "Resume" : "Pause"}
              </button>
              <button type="button" className="btn-stop" onClick={recorder.stop}>
                Stop
              </button>
            </div>
          </div>
        ) : recorder.status === "review" ? (
          <div className="rec-panel">
            <p className="rec-note">
              Reached your {maxRecordingMinutes}-minute limit. Upload this recording?
            </p>
            <div className="rec-controls">
              <button type="button" className="btn-ghost" onClick={recorder.discard}>
                Discard
              </button>
              <button type="button" className="btn-fill" onClick={recorder.uploadReview}>
                Upload
              </button>
            </div>
          </div>
        ) : (
          canRecord && (
            <button
              type="button"
              className="btn-record"
              onClick={recorder.start}
              disabled={uploading}
            >
              <span className={recordDotClass("idle")} aria-hidden />
              <span>Record</span>
            </button>
          )
        )}

        {/* A real button (not a label wrapping the input) so keyboard and voice
            control can reach it; the hidden input only hosts the file picker. */}
        <button
          type="button"
          className="btn-primary"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
        >
          <span>{uploading ? "Uploading…" : "Upload audio"}</span>
          <span className="plus" aria-hidden>
            +
          </span>
          {uploading && (
            <div className="progress upload-pulse" aria-hidden>
              <i />
            </div>
          )}
        </button>
        <input
          ref={inputRef}
          type="file"
          // iOS Files greys out Voice Memos (.m4a) under the bare audio/* wildcard;
          // explicit extensions un-grey them. Kept in sync with ALLOWED_EXTS in api.ts.
          accept="audio/*,video/mp4,.mp3,.wav,.m4a,.aac,.ogg,.opus,.flac,.webm,.aiff,.mp4"
          disabled={uploading}
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void handleFile(f);
          }}
        />
      </div>
      {(error || recorder.error) && (
        <p className="upload-error" role="alert">
          {error ?? recorder.error}
        </p>
      )}

      <h2 className="eyebrow">Recordings</h2>
      {transcripts.length === 0 ? (
        <p className="empty">No transcripts yet.</p>
      ) : (
        <ul className="transcript-list">
          {transcripts.map((t) => {
            const chip = CHIP[t.state];
            return (
              <li key={t.id}>
                <button
                  className={
                    `transcript-item${t.id === selectedId ? " selected" : ""}` +
                    (t.state === "failed" ? " failed" : "")
                  }
                  onClick={() => onSelect(t.id)}
                >
                  <span className="name">{t.name}</span>
                  <div className="row">
                    <span className={chip.cls}>{chip.label}</span>
                    <span className="meta">
                      {t.audioDurationSecs != null && `${formatTimestamp(t.audioDurationSecs)} · `}
                      {formatDateTime(t.createdAt)}
                    </span>
                  </div>
                  {/* decorative: the chip already carries the Transcribing state */}
                  {t.state === "processing" && (
                    <div className="progress" aria-hidden>
                      <i />
                    </div>
                  )}
                  {t.state === "failed" && t.error && (
                    <div className="fail-msg">{humaneError(t.error)}</div>
                  )}
                </button>
                <div className="item-menu">
                  <button
                    className="item-kebab"
                    aria-label={`Actions for ${t.name}`}
                    aria-expanded={menuId === t.id}
                    onClick={() => setMenuId((id) => (id === t.id ? null : t.id))}
                  >
                    ⋯
                  </button>
                  {/* Deliberately not role="menu": that role promises arrow-key
                      navigation we don't implement. Plain buttons Tab fine. */}
                  {menuId === t.id && (
                    <div className="item-dropdown">
                      <button onClick={() => void rename(t)}>Rename</button>
                      {canRetry(t.state) && <button onClick={() => void retry(t)}>Retry transcription</button>}
                      <button onClick={() => void copy(t)}>Copy to clipboard</button>
                      <button className="danger" onClick={() => void remove(t)}>
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}
